
import { defineComponent } from 'vue';
import { mapState, mapActions } from 'pinia';
import { required } from '@white-label-helper/vuelidate';
import type VueI18n from 'vue-i18n';
import type { Time, Terminal } from '@white-label-types/component';
import { selectDate } from 'ecommerce/shared-helpers';
import {
  format,
  add,
  differenceInMinutes,
  isEqual,
  isAfter,
  isBefore,
  isDate,
  DATE_TIME_FORMATS,
  utcToZonedTime,
  parseISO,
  guessTimezone
} from '@white-label-helper/date-utilities';
import {
  PARKINGS_TIMEPICKER_STEPS,
  SEARCH_BOX_SESSION_DURATION,
  NAMED_ROUTES,
  PARTNER_TYPES_CODES,
  CANT_FIND_FLIGHT_ID,
  PRODUCT_TYPES,
  FEATURE_FLAGS,
} from '@white-label-configuration/constants';
import { trackCheckoutStep } from '@white-label-helper/shared-helpers';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { useSearchBoxStore } from '@white-label-store/search-box';
import { useUserInfoStore } from '@white-label-store/user-info';
import { getAppHeroProduct } from '@white-label-helper/get-app-hero-product';

import type {
  ParkingFormData,
  QueryParams
} from '@white-label-types/search-box';
import { Partners } from '@white-label-types/partners';
import { isBookingPortal } from '@white-label-helper/is-booking-portal';
import { getCookieDomain } from '@white-label-helper/get-cookie-domain';

import IconCheck from '@white-label-icon/icon-check';
import IconAskMark from '@white-label-icon/icon-ask-mark';
import CustomersSelector from '../customers-selector/customers-selector.vue';
import { DropdownWrapper, TextField, IconMessage, Backdrop, FlightLookup } from 'ui-shared-components';
import DateTimePicker from '../date-time-picker/date-time-picker.vue';
import AirportSelect from '../airport-select/airport-select.vue';
import { HTMLElementWithDropdown } from '../../types/types';
import type { SerializeOptions as CookieSerializeOptions } from 'cookie';
import type { CustomersOptions } from '@white-label-types/customers';
import { getFeatureFlag, isInExperiment } from '@white-label-plugin/launch-darkly';
import { closeModal, popstateEventHandler, pushModalState } from '@white-label-helper/helper-history-modal';
import { pushToRouter } from '@white-label-helper/router';

export default defineComponent({
  name: 'SearchBoxParking',

  components: {
    DropdownWrapper,
    DateTimePicker,
    IconMessage,
    AirportSelect,
    TextField,
    IconCheck,
    IconAskMark,
    CustomersSelector,
    Backdrop,
    FlightLookup
  },

  data() {
    return {
      removePopstateHandlerCustomers: null,
      removePopstateHandlerTerminal: null,
      PARKINGS_TIMEPICKER_STEPS,
      formData: {
        airport: '',
        entryDate: '',
        entryTime: null,
        exitDate: '',
        exitTime: null,
        terminal: '',
        discount: null,
        outboundFlight: null,
        customers: {
          adult: 1,
          child: 0,
          senior: 0,
          infant: 0
        }
      } as ParkingFormData,
      maxDatePickerDate: add(new Date(), { years: 2 }) as Date,
      terminalOpened: false,
      discountFromQuery: null as null | string,
      exitDateBeforeEntryDate: false,
      customersOpened: false,
      customersError: '',
    };
  },

  validations() {
    const validationObject = {
      formData: {
        entryDate: { required },
        entryTime: { required },
        exitDate: { required },
        exitTime: { required },
        terminal: {},
        airport: {}
      }
    };

    if (this.isMultiAirportPartner) {
      validationObject.formData.airport = { required };
    }

    if (
      (this.isSingleAirportTerminalsExist && !this.isMultiAirportPartner) ||
      (this.isMultiAirportPartner && this.isMultiAirportTerminalsExist)
    ) {
      validationObject.formData.terminal = { required };
    }

    if (this.showGuestsSelector) {
      validationObject.formData.customers = {
        validation: (value: {
          child: number;
          infant: number;
          adult: number;
          senior: number;
          children_ages: number[];
        }) => {
          this.customersError = '';
          if (!this.isCustomerGroupsExists) return true;
          if (!Object.values(value).some((item) => item > 0)) {
            this.customersError = this.$t('validationError.selectNumberOfGuests');
            return false;
          }
          if (this.isChildAgeConfigurationAvailable && value.child > 0 && (value.children_ages?.length || 0) < value.child) {
            this.customersError = this.$t('guestDetails.ageIsRequired');
            return false;
          }

          if ((parseInt(value.senior, 10) || 0) + (parseInt(value.adult, 10) || 0) === 0) {
            this.customersError = this.$t('validationError.oneAdultRequired');
            return false;
          }
          return true;
        }
      };
    }

    return validationObject;
  },

  computed: {
    ...mapState(useSearchBoxStore, ['searchBoxFormData', 'searchBoxTimestamp']),
    ...mapState(useUserInfoStore, {
      outboundFlightLookupItems: 'readOutboundFlightLookupItems',
      outboundFlightSelected: 'readOutboundFlightLookupSelected',
    }),
    outboundFlight() {
      return this.formData.outboundFlight || this.outboundFlightSelected;
    },
    pageTitle(): string | VueI18n.TranslateResult {
      if (isInExperiment('CRO_928_L4P_STRIPPED_BACK_SEARCH_PAGE_HEADING')) {
        const flagValue = getFeatureFlag('CRO_928_L4P_STRIPPED_BACK_SEARCH_PAGE_HEADING').toLowerCase();
        return this.$t(`searchBox.parkingTitle.${flagValue}`);
      }

      return getAppVariable(`page_title[${this.$i18n.locale}]`, this.$t('searchBox.title'));
    },
    isMultiAirportPartner(): boolean {
      if (isBookingPortal) {
        return Array.isArray(this.airports) && this.airports.length > 1;
      }
      return (
        getAppVariable('partner_type_code') === PARTNER_TYPES_CODES.WL_ONLY
      );
    },

    airports(): Partners['pois'] {
      return getAppVariable('pois');
    },

    showSingleAirportTerminalSelect(): boolean {
      return this.isSingleAirportTerminalsExist && !this.isMultiAirportPartner;
    },

    showMultiAirportTerminalSelect(): boolean {
      return (
        this.isMultiAirportPartner &&
        this.formData.airport &&
        this.isMultiAirportTerminalsExist
      );
    },

    partnerTimeZone(): string {
      return getAppVariable('poi.timezone') || guessTimezone();
    },

    singleAirportTerminals(): Terminal[] {
      return getAppVariable('terminals');
    },

    singleAirportTerminalsWithDefault(): Terminal[] {
      return [
        ...this.singleAirportTerminals,
        {
          name: this.$t('searchBox.dontKnowOption'),
          id: 'dontKnow'
        }
      ];
    },

    multiAirportTerminals(): Terminal[] {
      const allSelectedTerminals: Terminal[] = getAppVariable('terminals');
      const airportTerminals = this.airports.find(
        (item) => item.code === this.formData.airport
      )?.terminals;

      if (airportTerminals) {
        return airportTerminals.filter((t1) =>
          allSelectedTerminals.some((t2) => t1.id === t2.id)
        );
      } else {
        return [];
      }
    },

    multiAirportTerminalsWithDefault(): Terminal[] {
      return [
        ...this.multiAirportTerminals,
        {
          name: this.$t('searchBox.dontKnowOption'),
          id: 'dontKnow'
        }
      ];
    },

    computedSingleAirportTerminal: {
      get(): Terminal {
        return (
          this.singleAirportTerminalsWithDefault.find(
            (i) => i.id.toString() === this.formData.terminal?.toString()
          ) || {
            name: '',
            id: ''
          }
        );
      },
      set(val: Terminal): void {
        this.formData.terminal = val.id.toString();

        if (this.showGuestsSelector && Object.values(this.formData.customers).every((val) => val === 0)) {
          this.customersOpened = true;
        }
      }
    },

    computedMultiAirportTerminal: {
      get(): Terminal {
        return (
          this.multiAirportTerminalsWithDefault.find(
            (i) => i.id.toString() === this.formData.terminal?.toString()
          ) || {
            name: '',
            id: ''
          }
        );
      },
      set(val: Terminal): void {
        this.formData.terminal = val.id.toString();

        // Automatically open entryDate field if empty
        if (!this.formData.entryDate && this.$refs.entryDate) {
          (this.$refs.entryDate as HTMLElementWithDropdown).dateDropdownOpened = true;
        }
      }
    },

    isSingleAirportTerminalsExist(): boolean {
      return this.singleAirportTerminals?.length > 1;
    },

    isMultiAirportTerminalsExist(): boolean {
      return this.multiAirportTerminals?.length > 1;
    },

    minExitDate(): Date {
      if (!this.formData.entryDate || !this.formData.entryTime?.value) {
        return new Date();
      }
      return add(
        parseISO(`${this.formData.entryDate}T${this.formData.entryTime.value}`),
        { hours: 1 },
      );
    },

    minDatePickerDate(): Date {
      const currentDateInPartnerTimeZone = utcToZonedTime(
        new Date(),
        this.partnerTimeZone
      );

      if (currentDateInPartnerTimeZone.getHours() >= 23) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.formData.entryDate = '';
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.formData.entryTime = null;
        return add(currentDateInPartnerTimeZone, { days: 1 });
      }
      return currentDateInPartnerTimeZone;
    },

    allDateTimeFieldsFilled(): boolean {
      const propertiesToKeep = [
        'entryDate',
        'entryTime',
        'exitDate',
        'exitTime'
      ];
      const dateTimeFields = propertiesToKeep.reduce((result, key) => {
        if (this.formData.hasOwnProperty(key)) {
          result[key] = this.formData[key];
        }
        return result;
      }, {});
      return !Object.values(dateTimeFields).some((item) => !item);
    },

    validationError(): boolean {
      return (
        this.$v.$error ||
        this.exitDateBeforeEntryDate ||
        this.exitTimeIsBeforeEntryTime ||
        this.entryDateBeforeCurrentDate
      );
    },
    exitTimeIsBeforeEntryTime(): boolean {
      const entryDate = parseISO(
        `${this.formData.entryDate} ${this.formData.entryTime?.value || ''}`
      );
      const exitDate = parseISO(
        `${this.formData.exitDate} ${this.formData.exitTime?.value || ''}`
      );
      const exitTimeIsSameOfBeforeAsEntry =
        isAfter(entryDate, exitDate) || isEqual(entryDate, exitDate);
      return this.allDateTimeFieldsFilled && exitTimeIsSameOfBeforeAsEntry;
    },
    entryDateBeforeCurrentDate(): boolean {
      const entryDate = parseISO(
        `${this.formData.entryDate} ${this.formData.entryTime?.value || ''}`
      );
      const currentDate = utcToZonedTime(new Date(), this.partnerTimeZone);

      const entryDateBeforeCurrentDate =
        isBefore(entryDate, currentDate) || isEqual(entryDate, currentDate);

      return this.allDateTimeFieldsFilled && entryDateBeforeCurrentDate;
    },
    displayEntryDateTimeCommonError(): boolean {
      return this.entryDateBeforeCurrentDate;
    },

    displayExitDateTimeCommonError(): boolean {
      return this.exitDateBeforeEntryDate || this.exitTimeIsBeforeEntryTime;
    },
    terminalError(): boolean {
      // @ts-ignore
      return !!this.$v.formData?.terminal?.$error;
    },

    airportError(): string {
      // @ts-ignore
      return this.$v.formData?.airport?.$error;
    },

    entryDateError(): boolean {
      // @ts-ignore
      return !!this.$v.formData?.entryDate?.$error;
    },

    entryTimeError(): boolean {
      // @ts-ignore
      return !!this.$v.formData?.entryTime?.$error;
    },

    entryDateTimeError(): VueI18n.TranslateResult {
      if (this.entryDateError && this.entryTimeError) {
        return this.$t('validationError.entryDateTime.dateTimeError');
      }
      if (this.entryDateError) {
        return this.$t('validationError.entryDateTime.dateError');
      }
      if (this.entryTimeError) {
        return this.$t('validationError.entryDateTime.timeError');
      }
      if (this.entryDateBeforeCurrentDate) {
        return this.$t('validationError.chooseFutureTime');
      }
      return '';
    },

    exitDateError(): boolean {
      // @ts-ignore
      return !!this.$v.formData?.exitDate?.$error;
    },

    exitTimeError(): boolean {
      // @ts-ignore
      return !!this.$v.formData?.exitTime?.$error;
    },

    exitDateTimeError(): VueI18n.TranslateResult {
      if (this.exitDateError && this.exitTimeError) {
        return this.$t('validationError.exitDateTime.dateTimeError');
      }
      if (this.exitDateError) {
        return this.$t('validationError.exitDateTime.dateError');
      }
      if (this.exitTimeError) {
        return this.$t('validationError.exitDateTime.timeError');
      }
      if (this.exitDateBeforeEntryDate) {
        return this.$t('validationError.exitDateBeforeEntryDate');
      }
      if (this.exitTimeIsBeforeEntryTime) {
        return this.$t('validationError.exitTimeIsBeforeEntryTime');
      }
      return '';
    },

    /**
     * A selection of classes to use for multi airport partner
     * @returns - The class
     */
    multiAirportClasses(): string {
      let classes = 'search-box search-box__multi-airport';

      if (this.isMultiAirportTerminalsExist) {
        classes += ' search-box__multi-terminals';
      }

      if (this.isDiscountFieldEnabled) {
        classes += ' search-box__discount';
      }

      if (this.showGuestsSelector) {
        classes += ' search-box__guests';
      }

      return classes;
    },

    /**
     * A selection of classes to use for single airport partner
     */
    singleAirportClasses(): string {
      let classes = 'search-box search-box__single-airport';

      if (this.isSingleAirportTerminalsExist) {
        classes += ' search-box__multi-terminals';
      }

      if (this.isDiscountFieldEnabled) {
        classes += ' search-box__discount';
      }

      if (this.showGuestsSelector) {
        classes += ' search-box__guests';
      }

      if (this.displayFlightLookup) {
        classes += ' search-box__flight-lookup';
      }
      if (this.displayFlightLookup && this.showGuestsSelector && this.isDiscountFieldEnabled) {
        classes += ' search-box__flight-lookup--compact';
      }

      return classes;
    },

    searchBoxClasses(): string {
      return this.isMultiAirportPartner ? this.multiAirportClasses : this.singleAirportClasses;
    },

    isDiscountFieldEnabled(): boolean {
      return getAppVariable(
        'discount_visibility_rules.is_enabled_in_landing_page'
      );
    },

    showGuestsSelector(): boolean {
      return this.$launchDarkly.variation('ECOM-2801_BUNDLES-PRE-PURCHASE') === true
        && this.isCustomerGroupsExists;
    },

    computedCustomers(): { name: string; id: string } {
      if (!this.formData.customers) {
        return { id: '', name: '' };
      }

      const { adult, senior, infant, child } = this.formData.customers;
      const label = [];

      const getAgeText = (key: 'senior' | 'adult' | 'child' | 'infant', count: number) => {
        return 'x ' + this.$tc(`shared.customers.${key}`, count);
      };

      if (senior) {
        label.push(`${senior} ${getAgeText('senior', senior)}`);
      }
      if (adult) {
        label.push(`${adult} ${getAgeText('adult', adult)}`);
      }
      if (child) {
        label.push(`${child} ${getAgeText('child', child)}`);
      }
      if (infant) {
        label.push(`${infant} ${getAgeText('infant', infant)}`);
      }

      return label.length
        ? { id: 'id', name: label.join(', ') }
        : {
          name: '',
          id: ''
        };
    },

    customerGroups(): CustomersOptions | null {
      const customerGroups = getAppVariable(`products.lounges.customer_groups`)
        || getAppVariable(`products.fasttrack.customer_groups`) as CustomersOptions;

      return customerGroups || null;
    },

    isCustomerGroupsExists(): boolean {
      return Boolean(
        this.customerGroups && Object.keys(this.customerGroups).length > 0
      );
    },

    customersBackdrop: {
      get(): boolean {
        return this.isMobileResolution && this.customersOpened;
      },
      set(): void {
        this.customersOpened = false;
      }
    },
    displayFlightLookup(): boolean {
      const isParkingProduct = getAppHeroProduct(PRODUCT_TYPES.PARKING) === PRODUCT_TYPES.PARKING;
      const displayFlightLookupForParking = isParkingProduct && getFeatureFlag(FEATURE_FLAGS.PT_2857_FLIGHT_LOOKUP_PARKING) === true;
      return getFeatureFlag(FEATURE_FLAGS.PT_2857_FLIGHT_LOOKUP) === true
        && getAppVariable('is_flight_lookup_enabled')
        && displayFlightLookupForParking;
    },

    flightLookupEligibility(): boolean {
      return !!(
        this.displayFlightLookup &&
        this.formData.entryDate &&
        this.formData.entryTime &&
        (!this.isMultiAirportPartner || (this.isMultiAirportPartner && this.formData.airport))
      );
    },

    disableCloseClickingOutsideGuestsDropdown(): boolean {
      return getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') && !this.isMobileResolution;
    },
    isChildAgeConfigurationAvailable(){
      return Boolean(this.customerGroups?.child_age_configuration);
    },
  },

  watch: {
    formData: {
      deep: true,
      handler(formData) {
        this.updateStoreData({
          formData,
          timestamp: new Date()
        });
      }
    },
    customersOpened: {
      handler(value) {
        if(!getFeatureFlag('CRO_887_BACK-MODALS-FUNCTIONALITY')){
          return;
        }
        if (value) {
          pushModalState('customersOpened');
        } else {
          if (
              getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') === true &&
              this.$refs.customersSelector &&
              !this.$refs.customersSelector.isCustomerChildSelectorsValid()
          ) {
              this.customersOpened = true;
              this.$refs.customersSelector.apply();
          }
          closeModal();
        }
      }
    },
    terminalOpened: {
      handler(value) {
        if(!getFeatureFlag('CRO_887_BACK-MODALS-FUNCTIONALITY')){
          return;
        }
        if (value) {
          pushModalState('terminalOpened');
        } else {
          closeModal();
        }
      }
    },
    'formData.airport': function(val) {
      if (!this.multiAirportTerminalsWithDefault.find((item) => item.id === parseInt(this.formData.terminal))) {
        this.formData.terminal = '';
      }

      if (this.multiAirportTerminals.length && !this.formData.terminal) {
        this.terminalOpened = true;
      } else if (!this.formData.entryDate && this.$refs.entryDate) {
        (this.$refs.entryDate as HTMLElementWithDropdown).dateDropdownOpened = true;
      }
    },
    flightLookupEligibility(val) {
      if (val) {
        this.getFlightItems();
      }
    },
  },
  beforeDestroy() {
    if (getFeatureFlag('CRO_887_BACK-MODALS-FUNCTIONALITY')) {
      if (this.removePopstateHandlerCustomers) {
        this.removePopstateHandlerCustomers();
      }
      if (this.removePopstateHandlerTerminal) {
        this.removePopstateHandlerTerminal();
      }
    }
  },
  mounted() {
    this.loadStoredData();

    if (typeof this.$route.query?.discount === 'string') {
      this.formData.discount = this.$route.query.discount;
      this.discountFromQuery = this.$route.query.discount;
    }

    if (this.$route.query['airport']) {
      this.formData.airport = this.$route.query['airport'];
    }

    if (this.$route.query['terminal']) {
      this.formData.terminal = this.$route.query['terminal'] as string;
    }

    if (this.$route.query['entryDate']) {
      this.formData.entryDate = this.$route.query['entryDate'] as string;
      if (this.$route.query['entryTime']) {
        this.formData.entryTime = {
          value: this.$route.query['entryTime'] as string,
          name: this.$route.query['entryTime'] as string
        };
      }
    }

    if (this.$route.query['exitDate']) {
      this.formData.exitDate = this.$route.query['exitDate'] as string;
      if (this.$route.query['exitTime']) {
        this.formData.exitTime = {
          value: this.$route.query['exitTime'] as string,
          name: this.$route.query['exitTime'] as string
        };
      }
    }

    if (this.$route.query['groups']) {
      const groups = JSON.parse(this.$route.query['groups'] as string);
      this.formData.customers = {
        adult: groups?.adult || 0,
        senior: groups?.senior || 0,
        infant: groups?.infant || 0,
        child: groups?.child || 0
      };

      if (this.$route.query['children_ages']) {
        this.formData.customers.children_ages = this.$route.query['children_ages'];
      }
    }

    if (typeof this.$route.query.clickref === 'string') {
      const options = {
        maxAge: 1800,
        path: '/',
        sameSite: true
      } as CookieSerializeOptions;

      if (process.env.NODE_ENV === 'production') {
        options.domain = getCookieDomain();
      }
      this.$cookies.set('partnerizeClickReference', this.$route.query.clickref, options);
    }

    this.$nextTick(() => {
      if (!this.$route.query['entryDate']) {
        selectDate(this.$refs.entryDate.$el, this.formData.entryDate);
      }

      if (!this.$route.query['entryDate']) {
        selectDate(this.$refs.exitDate.$el, this.formData.exitDate);
      }
    });

    if (getFeatureFlag('CRO_887_BACK-MODALS-FUNCTIONALITY')) {
      this.removePopstateHandlerCustomers = popstateEventHandler('customersOpened', this.onCustomersSelectorChange);
      this.removePopstateHandlerTerminal = popstateEventHandler('terminalOpened', () => this.terminalOpened = false);
      this.customersOpened = window.location.hash?.includes('customersOpened');
      this.terminalOpened = window.location.hash?.includes('terminalOpened');
    }
  },

  methods: {
    ...mapActions(useSearchBoxStore, ['updateStoreData']),
    ...mapActions(useUserInfoStore, {
      fetchFlightLookupDetails: 'fetchFlightLookupDetails',
      storeOutboundFlightLookup: 'selectOutboundFlightLookup',
    }),
    onFocusEvent(type: string): void {
      if (type === 'entryDateTime' || type === 'exitDateTime') {
        this.closeDropdowns(type);
        this.closeAirportDropdown();
        this.terminalOpened = false;
      }

      if (type === 'selectTerminal') {
        this.closeDropdowns('entryDateTime');
        this.closeDropdowns('exitDateTime');
        this.closeAirportDropdown();
      }
    },

    closeDropdowns(type: string): void {
      const ref =
        type === 'entryDateTime' ? this.$refs.exitDate : this.$refs.entryDate;
      (ref as HTMLElementWithDropdown).timeDropdownOpened = false;
      (ref as HTMLElementWithDropdown).dateDropdownOpened = false;
    },

    closeAirportDropdown(): void {
      if (this.$refs.selectAirport as HTMLElement) {
        (this.$refs.selectAirport as HTMLElement).dropdownOpened = false;
      }
    },

    loadStoredData(): void {
      const timestamp = this.searchBoxTimestamp;
      const formData = this.searchBoxFormData;

      if (formData.discount) {
        this.discountFromQuery = formData.discount;
      }

      const timestampDate: Date = (
        isDate(timestamp) ? timestamp : parseISO(timestamp as string)
      ) as Date;
      const sessionDuration = differenceInMinutes(new Date(), timestampDate);

      if (formData && sessionDuration < SEARCH_BOX_SESSION_DURATION) {
        this.formData = { ...formData };
      }
    },

    onEntryDateChange(date: string): void {
      const isEntryDateChanged = this.formData.entryDate !== date;
      this.formData.entryDate = date;

      const exitDateBeforeEntryDate =
        this.formData.exitDate &&
        isAfter(parseISO(date), parseISO(this.formData.exitDate));

      const entryTimeIsNotAvailable =
        this.formData.entryTime &&
        isBefore(
          parseISO(`${date} ${this.formData.entryTime.value}`),
          new Date()
        );

      if (exitDateBeforeEntryDate) {
        this.formData.exitDate = '';
        this.exitDateBeforeEntryDate = true;
      }
      if (entryTimeIsNotAvailable) {
        this.formData.entryTime = null;
      }

      // Automatically open entryTime field if empty
      if (!this.formData.entryTime && this.$refs.entryDate) {
        (this.$refs.entryDate as HTMLElementWithDropdown).timeDropdownOpened = true;
      }

      // Reset selected flight and get new flight items
      if (this.displayFlightLookup && isEntryDateChanged) {
        this.selectOutboundFlightLookup(null);
        this.getFlightItems();
        this.storeOutboundFlightLookup(this.outboundFlight);
      }
    },

    onEntryTimeChange(time: Time | null): void {
      const isEntryTimeChanged = this.formData.entryTime !== time;
      this.formData.entryTime = time;

      // Automatically open exitDate field if empty
      if (!this.formData.exitDate && this.$refs.exitDate) {
        (this.$refs.exitDate as HTMLElementWithDropdown).dateDropdownOpened = true;
      }

      // Reset selected flight and get new flight items
      if (this.displayFlightLookup && isEntryTimeChanged) {
        this.selectOutboundFlightLookup(null);
        this.getFlightItems();
        this.storeOutboundFlightLookup(this.outboundFlight);
      }
    },

    onExitDateChange(date: string): void {
      this.formData.exitDate = date;
      this.exitDateBeforeEntryDate = false;

      const exitTimeIsNotAvailable =
        this.formData.exitTime &&
        isBefore(
          parseISO(`${date} ${this.formData.exitTime.value}`),
          new Date()
        );

      if (exitTimeIsNotAvailable) {
        this.formData.exitTime = null;
      }

      // Automatically open exitTime field if empty
      if (!this.formData.exitTime && this.$refs.exitDate) {
        (this.$refs.exitDate as HTMLElementWithDropdown).timeDropdownOpened = true;
      }
    },

    onExitTimeChange(time: Time | null): void {
      this.formData.exitTime = time;

      // Automatically open terminal field if empty
      if (!this.formData.terminal && !this.isMultiAirportPartner) {
        this.terminalOpened = true;
        return;
      }

      const isDefaultCustomersValue = (this.formData.customers.adult || 0) === 1 &&
        (this.formData.customers.child || 0) === 0 &&
        (this.formData.customers.senior || 0) === 0 &&
        (this.formData.customers.infant || 0) === 0;
      if (this.showGuestsSelector && isDefaultCustomersValue) {
        this.customersOpened = true;
      }
    },

    onCustomersSelectorChange($event: { [key: string]: any }) {
      this.customersOpened = false;
    },

    onCustomersUpdated($event: any) {
      if (!getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') || this.isMobileResolution) {
        return;
      }

      this.formData.customers = $event;
    },

    outsideBackdropClicked() {
      if (!getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') || this.isMobileResolution) {
        this.customersOpened = false;
        return;
      }

      if (this.$refs.customersSelector) {
        this.$refs.customersSelector.apply();
      }
    },

    submitForm(): void {
      if (getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') && !this.isMobileResolution) {
        if (this.$refs.customersSelector) {
          this.$refs.customersSelector.apply();
        }
      }
      // @ts-ignore
      this.$v.formData.$touch();

      if (this.isMultiAirportPartner) {
        if (this.multiAirportTerminals.length === 1) {
          // If we have only one terminal we take it by default
          this.formData.terminal = this.multiAirportTerminals[0].id.toString();
        }
        if (this.multiAirportTerminals.length === 0) {
          // If there are no terminals, we take the first item from 'multiAirportTerminalsWithDefault'
          // this variable always has at least one element - 'Don't know'
          this.formData.terminal =
            this.multiAirportTerminalsWithDefault[0].id.toString();
        }
      } else {
        if (this.singleAirportTerminals.length === 1) {
          // If we have only one terminal we take it by default
          this.formData.terminal = this.singleAirportTerminals[0].id.toString();
        }
        if (this.singleAirportTerminals.length === 0) {
          // If there are no terminals, we take the first item from 'singleAirportTerminalsWithDefault'
          // this variable always has at least one element - 'Don't know'
          this.formData.terminal =
            this.singleAirportTerminalsWithDefault[0].id.toString();
        }
      }

      if (this.displayFlightLookup && !this.formData.terminal) {
        this.formData.terminal = 'dontKnow';
      }

      if (!this.validationError) {
        const terminal =
          typeof this.formData.terminal === 'string'
            ? this.formData.terminal
            : '';

        const queryParams: QueryParams = {
          entryDate: this.formData.entryDate || '',
          entryTime: this.formData.entryTime?.value || '',
          exitDate: this.formData.exitDate || '',
          exitTime: this.formData.exitTime?.value || '',
        };

        if (this.formData.discount) {
          queryParams.discount = this.formData.discount;
        }
        if (this.formData.airport) {
          queryParams.airport = this.formData.airport as string;
        }
        if (terminal) {
          queryParams.terminal = terminal;
        }
        if (this.outboundFlight) {
          queryParams.outboundFlight = this.outboundFlight.id;
        }
        if (this.$route.query['utm_source']) {
          queryParams.utmSource = this.$route.query['utm_source'] as string;
        }
        if (this.$route.query['utm_medium']) {
          queryParams.utmMedium = this.$route.query['utm_medium'] as string;
        }
        if (this.showGuestsSelector) {
          const { customers } = this.formData;
          const groups = {};

          Object.keys(customers)
            .filter((key) => customers[key])
            .forEach((key: string) => {
              if (key !== 'children_ages') {
                groups[key] = customers[key];
              } else {
                queryParams[key] = customers[key];
              }
            });
          queryParams.groups = JSON.stringify(groups);
        }

        trackCheckoutStep(this.$gtm, 1, 'Select dates');
        pushToRouter({ path: NAMED_ROUTES.search, query: queryParams });
      }
    },

    selectOutboundFlightLookup(flight): void {
      if (flight && flight?.id !== CANT_FIND_FLIGHT_ID) {
        this.formData.outboundFlight = flight;
      } else if (flight && flight?.id === CANT_FIND_FLIGHT_ID) {
        this.formData.outboundFlight = { id: CANT_FIND_FLIGHT_ID };
      } else {
        this.formData.outboundFlight = null;
      }
      this.formData.terminal = flight?.terminal ?? 'dontKnow';
      this.storeOutboundFlightLookup(flight);
    },
    getFlightItems() {
      if (!this.formData.entryDate || !this.formData.entryTime?.value) return;
      const dateTimeISO = `${this.formData.entryDate}T${this.formData.entryTime.value}:00.000Z`;
      const zonedDate = utcToZonedTime(new Date(dateTimeISO), 'UTC');
      const datetime = format(zonedDate, DATE_TIME_FORMATS.year_month_day_utc);
      const airportCode = this.formData.airport || getAppVariable('poi.code');
      this.fetchFlightLookupDetails({
        datetime,
        airportCode,
        direction: 'departure'
      })
    },
    onChildNumberUpdated($event: { [key: string]: any }) {
      if (getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') === true) {
        this.formData.customers.child = $event;
        this.formData.customers.children_ages = this.formData.customers.children_ages?.slice(0, this.formData.customers.child) ?? [];
      }
    }
  }
});
